// Printing & design helpers: print resolution, poster/canvas/frame sizing,
// bleed/margins, photo print sizes, social media crops, paper sizes,
// image scaling, responsive image widths, and grid layout math.
import { t } from './i18n'

// "Image Print Resolution (DPI) Calculator" - reports the limiting
// (smaller of horizontal/vertical) DPI, since if width and height don't
// scale to the exact same DPI, the lower one determines print quality.
const DPI_QUALITY: Record<string, Record<string, string>> = {
    excellent: { en: 'Excellent (Photo Quality)', ru: 'Отлично (фотокачество)', de: 'Ausgezeichnet (Fotoqualität)', es: 'Excelente (Calidad Foto)', fr: 'Excellent (Qualité Photo)', it: 'Eccellente (Qualità Foto)', pl: 'Doskonała (Jakość Foto)', lv: 'Izcila (Foto Kvalitāte)' },
    good: { en: 'Good', ru: 'Хорошо', de: 'Gut', es: 'Bueno', fr: 'Bon', it: 'Buono', pl: 'Dobra', lv: 'Laba' },
    fair: { en: 'Fair (OK for posters viewed at a distance)', ru: 'Приемлемо (подходит для плакатов на расстоянии)', de: 'Ausreichend (OK für Poster aus der Ferne)', es: 'Aceptable (Bien para pósteres vistos a distancia)', fr: 'Acceptable (Correct pour affiches vues de loin)', it: 'Accettabile (Va bene per poster visti a distanza)', pl: 'Zadowalająca (OK dla plakatów oglądanych z odległości)', lv: 'Pieņemama (Der plakātiem, ko skata no attāluma)' },
    poor: { en: 'Poor (Will look pixelated)', ru: 'Плохо (будет выглядеть пиксельно)', de: 'Schlecht (wirkt pixelig)', es: 'Deficiente (Se verá pixelado)', fr: 'Mauvais (Aura l\'air pixelisé)', it: 'Scarsa (Apparirà pixelata)', pl: 'Słaba (Będzie wyglądać pikselowo)', lv: 'Slikta (Izskatīsies pikseļota)' },
}
export function dpiCalculator(params: {
    pixel_width: unknown; pixel_height: unknown; print_width: unknown; print_height: unknown; unit: unknown; language: unknown
}): { dpi_horizontal: number; dpi_vertical: number; effective_dpi: number; quality: string } {
    const pixelWidth = Number(params.pixel_width) || 0
    const pixelHeight = Number(params.pixel_height) || 0
    const printWidth = Number(params.print_width) || 0
    const printHeight = Number(params.print_height) || 0
    const isCm = String(params.unit) === 'cm'
    const widthIn = isCm ? printWidth / 2.54 : printWidth
    const heightIn = isCm ? printHeight / 2.54 : printHeight
    const dpiHorizontal = widthIn > 0 ? pixelWidth / widthIn : 0
    const dpiVertical = heightIn > 0 ? pixelHeight / heightIn : 0
    const effectiveDpi = Math.min(dpiHorizontal, dpiVertical)
    let bucket: string
    if (effectiveDpi >= 300) bucket = 'excellent'
    else if (effectiveDpi >= 200) bucket = 'good'
    else if (effectiveDpi >= 150) bucket = 'fair'
    else bucket = 'poor'
    return { dpi_horizontal: dpiHorizontal, dpi_vertical: dpiVertical, effective_dpi: effectiveDpi, quality: t(DPI_QUALITY[bucket], params.language) }
}

// "Poster Size Finder" - finds the largest print size (in either orientation)
// an image supports at a target DPI, plus the closest standard poster size
// that fits without exceeding it. Standard sizes are checked in whichever
// orientation (portrait/landscape) matches the image, not just as listed.
const STANDARD_POSTER_SIZES_IN: Array<[number, number]> = [
    [8, 10], [11, 14], [11, 17], [16, 20], [18, 24], [24, 36], [27, 40],
]
export function posterSizeFinder(params: {
    pixel_width: unknown; pixel_height: unknown; target_dpi: unknown; unit: unknown
}): { max_width: number; max_height: number; best_fit_width: number | null; best_fit_height: number | null } {
    const pixelWidth = Number(params.pixel_width) || 0
    const pixelHeight = Number(params.pixel_height) || 0
    const targetDpi = Number(params.target_dpi) || 300
    const maxWidthIn = pixelWidth / targetDpi
    const maxHeightIn = pixelHeight / targetDpi
    const isLandscape = maxWidthIn >= maxHeightIn
    let best: [number, number] | null = null
    for (const [wp, hp] of STANDARD_POSTER_SIZES_IN) {
        const w = isLandscape ? Math.max(wp, hp) : Math.min(wp, hp)
        const h = isLandscape ? Math.min(wp, hp) : Math.max(wp, hp)
        if (w <= maxWidthIn && h <= maxHeightIn) {
            if (!best || w * h > best[0] * best[1]) best = [w, h]
        }
    }
    const factor = String(params.unit) === 'cm' ? 2.54 : 1
    return {
        max_width: maxWidthIn * factor,
        max_height: maxHeightIn * factor,
        best_fit_width: best ? best[0] * factor : null,
        best_fit_height: best ? best[1] * factor : null,
    }
}

// "Canvas Panel Splitter" - even horizontal split across N side-by-side
// canvas panels (e.g. a triptych), the standard convention for split-canvas
// prints; each panel keeps the full original height.
export function canvasPanelSplitter(params: { total_width: unknown; total_height: unknown; num_panels: unknown; gap: unknown }): {
    panel_width: number; panel_height: number; total_width_with_gaps: number
} {
    const totalWidth = Number(params.total_width) || 0
    const totalHeight = Number(params.total_height) || 0
    const numPanels = Math.max(Math.round(Number(params.num_panels)) || 2, 2)
    const gap = Number(params.gap) || 0
    return {
        panel_width: totalWidth / numPanels,
        panel_height: totalHeight,
        total_width_with_gaps: totalWidth + gap * (numPanels - 1),
    }
}

// "Picture Frame Mat Calculator" - assumes the image is centered in the
// frame; reports the border width on each axis separately since many mats
// (especially "bottom-weighted" designs) intentionally use different
// top/bottom vs. left/right border widths.
export function matCalculator(params: {
    frame_width: unknown; frame_height: unknown; image_width: unknown; image_height: unknown
}): { border_horizontal: number; border_vertical: number } {
    const frameWidth = Number(params.frame_width) || 0
    const frameHeight = Number(params.frame_height) || 0
    const imageWidth = Number(params.image_width) || 0
    const imageHeight = Number(params.image_height) || 0
    return {
        border_horizontal: (frameWidth - imageWidth) / 2,
        border_vertical: (frameHeight - imageHeight) / 2,
    }
}

// "Bleed & Trim Size Calculator" - bleed is added symmetrically on all sides
export function bleedCalculator(params: { trim_width: unknown; trim_height: unknown; bleed_amount: unknown }): {
    bleed_width: number; bleed_height: number
} {
    const trimWidth = Number(params.trim_width) || 0
    const trimHeight = Number(params.trim_height) || 0
    const bleedAmount = Number(params.bleed_amount) || 0
    return { bleed_width: trimWidth + 2 * bleedAmount, bleed_height: trimHeight + 2 * bleedAmount }
}

// "Safe Margin Calculator" - safe area is inset symmetrically from the trim edge
export function safeMarginCalculator(params: { trim_width: unknown; trim_height: unknown; safe_margin: unknown }): {
    safe_width: number; safe_height: number
} {
    const trimWidth = Number(params.trim_width) || 0
    const trimHeight = Number(params.trim_height) || 0
    const safeMargin = Number(params.safe_margin) || 0
    return { safe_width: trimWidth - 2 * safeMargin, safe_height: trimHeight - 2 * safeMargin }
}

// "Photo Print Size Finder" - matches an image's aspect ratio against
// standard photo print sizes, checked in BOTH orientations (portrait and
// landscape) since a landscape photo should still match e.g. "6x4" even
// though the standard size list is written portrait-first.
const PHOTO_SIZES: Array<[number, number]> = [[4, 6], [5, 7], [8, 10], [8, 12], [11, 14], [16, 20], [20, 30]]
const PHOTO_NO_MATCH: Record<string, string> = {
    en: 'No standard photo print sizes match this aspect ratio exactly.', ru: 'Ни один стандартный размер печати фото точно не соответствует этому соотношению сторон.',
    de: 'Keine Standard-Fotogrößen entsprechen genau diesem Seitenverhältnis.', es: 'Ningún tamaño estándar de impresión coincide exactamente con esta relación de aspecto.',
    fr: 'Aucun format photo standard ne correspond exactement à ce rapport d\'aspect.', it: 'Nessun formato foto standard corrisponde esattamente a questo rapporto d\'aspetto.',
    pl: 'Żaden standardowy rozmiar wydruku zdjęcia nie pasuje dokładnie do tych proporcji.', lv: 'Neviens standarta fotoattēlu drukas izmērs precīzi neatbilst šai malu attiecībai.',
}
export function photoPrintSizeFinder(params: { pixel_width: unknown; pixel_height: unknown; language: unknown }): {
    image_ratio: number; megapixels: number; matches: string
} {
    const pixelWidth = Number(params.pixel_width) || 0
    const pixelHeight = Number(params.pixel_height) || 0
    const ratio = pixelHeight > 0 ? pixelWidth / pixelHeight : 0
    const matches: string[] = []
    for (const [w, h] of PHOTO_SIZES) {
        const portraitRatio = w / h
        const landscapeRatio = h / w
        if (ratio > 0 && Math.abs(portraitRatio - ratio) / ratio < 0.02) matches.push(`${w}x${h}`)
        else if (ratio > 0 && Math.abs(landscapeRatio - ratio) / ratio < 0.02) matches.push(`${h}x${w}`)
    }
    const megapixels = (pixelWidth * pixelHeight) / 1000000
    return { image_ratio: ratio, megapixels, matches: matches.length > 0 ? matches.join(', ') : t(PHOTO_NO_MATCH, params.language) }
}

// "Social Media Image Crop Calculator" - center-crops toward a platform's
// target aspect ratio, cropping the narrower dimension only (never
// stretching), and reports the crop box offset for positioning.
const PLATFORM_RATIOS: Record<string, number> = {
    instagram_square: 1,
    instagram_portrait: 4 / 5,
    instagram_story: 9 / 16,
    facebook_post: 1.91,
    twitter_post: 16 / 9,
    youtube_thumbnail: 16 / 9,
    linkedin_post: 1.91,
}
export function socialCropCalculator(params: { pixel_width: unknown; pixel_height: unknown; platform: unknown }): {
    crop_width: number; crop_height: number; offset_x: number; offset_y: number
} {
    const pixelWidth = Number(params.pixel_width) || 0
    const pixelHeight = Number(params.pixel_height) || 0
    const targetRatio = PLATFORM_RATIOS[String(params.platform)] || 1
    const imageRatio = pixelHeight > 0 ? pixelWidth / pixelHeight : 0
    let cropWidth: number
    let cropHeight: number
    if (imageRatio > targetRatio) {
        cropHeight = pixelHeight
        cropWidth = pixelHeight * targetRatio
    } else {
        cropWidth = pixelWidth
        cropHeight = pixelWidth / targetRatio
    }
    return {
        crop_width: cropWidth,
        crop_height: cropHeight,
        offset_x: (pixelWidth - cropWidth) / 2,
        offset_y: (pixelHeight - cropHeight) / 2,
    }
}

// "Paper Size Converter" - ISO A-series + common US sizes, converted to
// the requested unit (px conversion uses the given DPI)
const PAPER_SIZES_MM: Record<string, [number, number]> = {
    a0: [841, 1189], a1: [594, 841], a2: [420, 594], a3: [297, 420], a4: [210, 297], a5: [148, 210],
    us_letter: [215.9, 279.4], us_legal: [215.9, 355.6], tabloid: [279.4, 431.8],
}
export function paperSizeConverter(params: { paper_size: unknown; output_unit: unknown; dpi: unknown }): {
    width: number; height: number
} {
    const [wMm, hMm] = PAPER_SIZES_MM[String(params.paper_size)] || PAPER_SIZES_MM.a4
    const dpi = Number(params.dpi) || 300
    const unit = String(params.output_unit)
    let width: number
    let height: number
    if (unit === 'mm') {
        width = wMm
        height = hMm
    } else if (unit === 'cm') {
        width = wMm / 10
        height = hMm / 10
    } else if (unit === 'in') {
        width = wMm / 25.4
        height = hMm / 25.4
    } else {
        width = (wMm / 25.4) * dpi
        height = (hMm / 25.4) * dpi
    }
    return { width, height }
}

// "Image Scale Factor Calculator" - solves for the new dimensions given
// either a target width, a target height, or a direct scale percentage
export function scaleFactorCalculator(params: {
    original_width: unknown; original_height: unknown; mode: unknown; target_value: unknown
}): { scale_percent: number; new_width: number; new_height: number } {
    const originalWidth = Number(params.original_width) || 0
    const originalHeight = Number(params.original_height) || 0
    const targetValue = Number(params.target_value) || 0
    const mode = String(params.mode)
    let scale: number
    if (mode === 'by_width') scale = originalWidth > 0 ? targetValue / originalWidth : 0
    else if (mode === 'by_height') scale = originalHeight > 0 ? targetValue / originalHeight : 0
    else scale = targetValue / 100
    return { scale_percent: scale * 100, new_width: originalWidth * scale, new_height: originalHeight * scale }
}

// "Responsive Image Size Calculator" - recommended export widths at
// standard pixel-density multipliers (1x/1.5x/2x/3x) for a given max
// CSS display width, for use in a srcset
export function responsiveImageSizeCalculator(params: { max_display_width: unknown }): {
    width_1x: number; width_1_5x: number; width_2x: number; width_3x: number
} {
    const maxDisplayWidth = Number(params.max_display_width) || 0
    return {
        width_1x: maxDisplayWidth,
        width_1_5x: maxDisplayWidth * 1.5,
        width_2x: maxDisplayWidth * 2,
        width_3x: maxDisplayWidth * 3,
    }
}

// "Grid Column Width Calculator"
export function gridColumnWidthCalculator(params: { total_width: unknown; num_columns: unknown; gutter_width: unknown }): number {
    const totalWidth = Number(params.total_width) || 0
    const numColumns = Math.max(Math.round(Number(params.num_columns)) || 1, 1)
    const gutterWidth = Number(params.gutter_width) || 0
    return (totalWidth - (numColumns - 1) * gutterWidth) / numColumns
}
